import { useCallback, useEffect, useRef, useState } from 'react'
import { Mic, RotateCcw, Square, Trash2 } from 'lucide-react'
import { VoiceMessage } from '@/components/VoiceMessage'
import {
  MAX_RECORDING_SECONDS,
  canRecord,
  describeMicrophoneError,
  formatDuration,
  pickRecordingMimeType,
  recordingToFile,
} from '@/lib/voiceRecording'
import { Button } from '@/components/ui'
import { cn } from '@/lib/utils'
import { useT } from '@/i18n/I18nProvider'

export interface VoiceNote {
  file: File
  durationSec: number
}

/**
 * Record a spoken message instead of typing one.
 *
 * The reason this exists is the same reason the QR stickers exist: the person
 * who knows the machine is standing next to it, wearing gloves, and a phone
 * keyboard is the slowest thing in the building. Saying "hobla se trese kad
 * krene i miriše na zagorjelo" takes four seconds. Typing it takes a minute,
 * and plenty of people simply will not.
 *
 * Three states, and the screen only ever shows one of them: nothing recorded,
 * recording, recorded. There is no "uploading" — the message rides along with
 * the report, so it is sent when the report is.
 */
export function VoiceRecorder({
  value,
  onChange,
  disabled,
}: {
  value: VoiceNote | null
  onChange: (value: VoiceNote | null) => void
  disabled?: boolean
}) {
  const t = useT()
  const [recording, setRecording] = useState(false)
  const [elapsed, setElapsed] = useState(0)
  const [level, setLevel] = useState(0)
  const [error, setError] = useState<string | null>(null)

  /*
   * Everything that has to be torn down, in one place.
   *
   * Held in a ref rather than state because teardown must be reachable from the
   * unmount cleanup, which never sees a re-render. A microphone left open is the
   * same failure the QR scanner had: on a phone that is the recording indicator
   * staying lit with the app apparently closed, and a worker reasonably
   * concluding the app is listening to them.
   */
  const session = useRef<{
    stream: MediaStream | null
    recorder: MediaRecorder | null
    audio: AudioContext | null
    frame: number | null
    tick: number | null
  }>({ stream: null, recorder: null, audio: null, frame: null, tick: null })

  const releaseEverything = useCallback(() => {
    const current = session.current

    if (current.frame !== null) cancelAnimationFrame(current.frame)
    if (current.tick !== null) clearInterval(current.tick)

    // close() rejects if the context is already closed — which happens whenever
    // teardown runs twice, and teardown running twice is normal.
    void current.audio?.close().catch(() => {})

    // The tracks are the microphone. Everything else is bookkeeping.
    current.stream?.getTracks().forEach((track) => track.stop())

    session.current = { stream: null, recorder: null, audio: null, frame: null, tick: null }
  }, [])

  useEffect(() => releaseEverything, [releaseEverything])

  async function start() {
    setError(null)

    if (!canRecord()) {
      setError(t('submissions.voice.unavailable'))
      return
    }

    let stream: MediaStream

    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch (cause) {
      setError(describeMicrophoneError(cause))
      return
    }

    const mimeType = pickRecordingMimeType()
    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined)
    const chunks: BlobPart[] = []

    // Counted here rather than read back off the file: a MediaRecorder writes no
    // duration into a WebM header, so its own output reports Infinity until it
    // is seeked. See lib/voiceRecording.ts.
    const startedAt = Date.now()

    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunks.push(event.data)
    }

    recorder.onstop = () => {
      const seconds = Math.max(1, Math.round((Date.now() - startedAt) / 1000))
      const blob = new Blob(chunks, { type: recorder.mimeType || mimeType || 'audio/webm' })

      releaseEverything()
      setRecording(false)
      setLevel(0)

      // A blob with nothing in it means the microphone produced no audio —
      // handing that to the form would send an empty file that plays as silence.
      if (blob.size === 0) {
        setError(t('submissions.voice.empty'))
        return
      }

      onChange({ file: recordingToFile(blob, recorder.mimeType || 'audio/webm'), durationSec: seconds })
    }

    session.current.stream = stream
    session.current.recorder = recorder

    session.current.tick = window.setInterval(() => {
      const seconds = Math.round((Date.now() - startedAt) / 1000)
      setElapsed(seconds)

      // Hard stop rather than a warning. An unbounded recording on a phone in a
      // pocket is how somebody sends forty minutes of factory noise.
      if (seconds >= MAX_RECORDING_SECONDS) recorder.stop()
    }, 250)

    startLevelMeter(stream)

    recorder.start()
    setElapsed(0)
    setRecording(true)
  }

  /**
   * A live level bar, so the answer to "is it hearing me" arrives while
   * recording rather than on playback.
   *
   * In a workshop that question is real — the machine next to you is louder than
   * you are, and a message that turns out to be silence is one nobody re-records
   * because nobody notices.
   */
  function startLevelMeter(stream: MediaStream) {
    try {
      const context = new AudioContext()
      const analyser = context.createAnalyser()
      analyser.fftSize = 256

      context.createMediaStreamSource(stream).connect(analyser)
      session.current.audio = context

      const samples = new Uint8Array(analyser.frequencyBinCount)

      const read = () => {
        analyser.getByteFrequencyData(samples)

        const average = samples.reduce((sum, value) => sum + value, 0) / samples.length
        setLevel(Math.min(1, average / 90))

        session.current.frame = requestAnimationFrame(read)
      }

      read()
    } catch {
      // A browser without AudioContext still records perfectly well. The meter
      // is reassurance, not function.
    }
  }

  function stop() {
    session.current.recorder?.stop()
  }

  function discard() {
    onChange(null)
    setError(null)
  }

  if (value) {
    return (
      <div className="space-y-2">
        <RecordedNote note={value} />

        <div className="flex gap-2">
          <Button type="button" variant="secondary" onClick={discard} disabled={disabled}>
            <RotateCcw className="size-4" aria-hidden />
            {t('submissions.voice.again')}
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={discard}
            disabled={disabled}
            aria-label={t('submissions.voice.discard')}
          >
            <Trash2 className="size-4" aria-hidden />
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {recording ? (
        <div className="flex items-center gap-3 rounded-xl border border-rose-300 bg-rose-50 px-3 py-2.5 dark:border-rose-900 dark:bg-rose-950/40">
          <span className="relative flex size-3 shrink-0" aria-hidden>
            <span className="absolute inline-flex size-full animate-ping rounded-full bg-rose-500 opacity-70" />
            <span className="relative inline-flex size-3 rounded-full bg-rose-600" />
          </span>

          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium tabular-nums" aria-live="off">
              {formatDuration(elapsed)}{' '}
              <span className="muted font-normal">
                / {formatDuration(MAX_RECORDING_SECONDS)}
              </span>
            </p>

            {/* Not decoration — see startLevelMeter. */}
            <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-rose-200 dark:bg-rose-900">
              <div
                className="h-full rounded-full bg-rose-600 transition-[width] duration-75"
                style={{ width: `${Math.round(level * 100)}%` }}
              />
            </div>
          </div>

          <Button type="button" variant="danger" onClick={stop}>
            <Square className="size-4 fill-current" aria-hidden />
            {t('submissions.voice.stop')}
          </Button>
        </div>
      ) : (
        <button
          type="button"
          onClick={start}
          disabled={disabled}
          className={cn(
            'flex min-h-11 w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed',
            'border-[var(--border-subtle)] px-4 py-3 text-sm transition-colors',
            'hover:bg-[var(--surface-sunken)] disabled:opacity-50',
          )}
        >
          <Mic className="size-5" aria-hidden />
          {t('submissions.voice.record')}
        </button>
      )}

      {error && (
        <p role="alert" className="text-xs font-medium text-rose-600">
          {error}
        </p>
      )}
    </div>
  )
}

/**
 * Playback of a recording that has not been sent yet.
 *
 * Shares `VoiceMessage` with the submission screen so a worker checking their
 * own recording and a supervisor hearing it later see the same control.
 */
function RecordedNote({ note }: { note: VoiceNote }) {
  const t = useT()

  /*
   * The file is handed over directly rather than as a loader.
   *
   * It used to create an object URL in an effect and pass a loader that read
   * it back. React runs child effects *before* parent ones, so VoiceMessage
   * asked for the URL before this effect had made one, the loader threw, and a
   * recording sitting in memory displayed "cannot load" with no way to retry.
   * VoiceMessage owns the object URL now, including revoking it.
   */
  return (
    <VoiceMessage
      label={t('submissions.voice.label')}
      durationSec={note.durationSec}
      mimeType={note.file.type}
      source={note.file}
    />
  )
}
